import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuthenticationVersion } from "providers/AuthenticationProvider";


type FetcherDataCacheContextType = {
  /**
   * If `hibernate` is true, the data will be preserved in the cache even after authentication changes.
   * Can be used to cache user-independent data.
   */
  cacheFetcherData: <T>(fetcherKey: string, data: T, hibernate?: boolean) => void;
  getFetcherData: <T>(fetcherKey: string) => T | undefined;
  clearFetcherData: (fetcherKey: string) => void;
};

let FetcherDataCacheContext = createContext<FetcherDataCacheContextType | undefined>(undefined);

/**
 * Simple Provider to cache data. We use this to hold stale data accessed by the `useFetcherData` hook, so components
 * can use that for rendering even after a fetcher submission has failed and `fetcher.data` holds the error. Also,
 * it can be useful when navigating between completely different routes where React Router would otherwise reload the
 * data after losing the internal state in `useDataFetcher`.
 */
export default function FetcherDataCacheProvider({ children }: { children: ReactNode}) {
  const cache = useRef<Map<string, any>>(new Map<string, any>());
  const authenticationVersion = useAuthenticationVersion();
  const oldAuthenticationVersion = useRef<number | undefined>();

  const [ hibernatedKeys, setHibernatedKeys ] = useState<Set<string>>(new Set<string>());

  // clear the cache on auth version changes, except for hibernated keys. works only if wrapped inside an
  // <AuthenticationProvider>
  useEffect(() => {
    if (authenticationVersion) {
      if (!oldAuthenticationVersion.current || (oldAuthenticationVersion.current !== authenticationVersion.version)) {
        oldAuthenticationVersion.current = authenticationVersion.version;
        for (const key of cache.current.keys()) {
          if (!hibernatedKeys.has(key)) {
            cache.current.delete(key);
          }
        }
      }
    }
  }, [ authenticationVersion, hibernatedKeys ]);

  const cacheStaleData = useCallback((key: string, data: any, hibernate?: boolean) => {
    cache.current.set(key, data);
    if (hibernate) {
      setHibernatedKeys(keys => keys.add(key));
    }
  }, []);

  const getStaleData = useCallback((key: string) => {
    return cache.current.get(key);
  }, []);

  const clearStaleData = useCallback((fetcherKey: string) => {
    cache.current.delete(fetcherKey);
  }, []);


  return (
    <FetcherDataCacheContext.Provider
      value={ {
        cacheFetcherData: cacheStaleData,
        getFetcherData: getStaleData,
        clearFetcherData: clearStaleData
      } }
    >
      { children }
    </FetcherDataCacheContext.Provider>
  );
}


export function useFetcherDataCache() {
  return useContext(FetcherDataCacheContext);
}
